import React, { useEffect } from "react";
import {
  TagIcon,
  DocumentTextIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { fetchUpcomingSubscription } from "../../../features/subscriptionSlice";
import { useParams } from "react-router-dom";
import EmptyUpcomingSubscription from "./EmptyUpcomingSubscription";

const UpcomingPlans = () => {
  const { userId } = useParams();
  const dispatch = useDispatch();

  const {
    upcomingSubscription,
    upcomingSubscriptionStatus,
    error
  } = useSelector((state) => state.subscription);

  useEffect(() => {
    if (userId) {
      dispatch(fetchUpcomingSubscription(userId));
    }
  }, [dispatch, userId]);

  if (upcomingSubscriptionStatus === 'loading') {
    return <div className="flex justify-center p-8">
      <div className="animate-pulse bg-gradient-to-r from-primary-shade1 to-primary-shade2 h-12 w-48 rounded-full" />
    </div>;
  }

  if (upcomingSubscriptionStatus === 'failed') {
    return <p className="text-red-500 text-center py-6">Error loading plans: {error}</p>;
  }

  if (!upcomingSubscription) {
    return <EmptyUpcomingSubscription />;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-16">
      <h2 className="text-4xl font-nunito font-extrabold text-center mb-12">
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-shade5 to-primary">
          Upcoming Plan
        </span>
      </h2>

      <div className="relative group">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary-shade2/10 rounded-[3rem] blur-xl group-hover:blur-2xl transition-all duration-300"></div>
        <div className="relative bg-gradient-to-br from-white to-primary-shade1 backdrop-blur-sm rounded-[3rem] p-8 shadow-2xl ring-1 ring-white/20 hover:shadow-3xl transition-all duration-300">
          <div className="mb-6">
            <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-shade5 to-primary">
              {upcomingSubscription.planName}
            </h3>
            <p className="text-sm text-text-muted/80 mt-1">
              Active from {formatDate(upcomingSubscription.startDate)}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <DetailItem
              icon={DocumentTextIcon}
              label="Package Type"
              value={upcomingSubscription.selectedPackageDetails?.packageType}
              gradient="from-blue-400 to-cyan-400"
            />
            <DetailItem
              icon={CurrencyRupeeIcon}
              label="Price"
              value={`₹${upcomingSubscription.selectedPackageDetails?.price}`}
              gradient="from-green-400 to-emerald-400"
            />
            <DetailItem
              icon={ClockIcon}
              label="Validity"
              value={`${upcomingSubscription.selectedPlan?.validityDays} Days`}
              gradient="from-purple-400 to-fuchsia-400"
            />
            <DetailItem
              icon={CalendarIcon}
              label="End Date"
              value={formatDate(upcomingSubscription.endDate)}
              gradient="from-orange-400 to-amber-400"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailItem = ({ icon: Icon, label, value, gradient }) => (
  <div className="flex items-start space-x-4">
    <div className={`flex-shrink-0 p-3 rounded-2xl bg-gradient-to-br ${gradient}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <div className="text-sm font-medium text-text-muted/80">{label}</div>
      <div className="text-lg font-semibold text-text-muted">{value || "N/A"}</div>
    </div>
  </div>
);

const formatDate = (dateArray) => {
  if (!Array.isArray(dateArray)) return "N/A";
  const [year, month, day] = dateArray;
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export default UpcomingPlans;